import React, { useEffect, useState } from "react"
import { useStore } from "../teafuljs/store"
import { getTranslation } from "../scripts/getTranslation"

const isStandaloneMode = () => {
  if (typeof window === "undefined") return false

  const iosStandalone =
    typeof window.navigator !== "undefined" &&
    window.navigator.standalone === true
  const displayModeStandalone =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(display-mode: standalone)").matches

  return iosStandalone || displayModeStandalone
}

const PWAInstallOverlay = () => {
  const [installPromptEvent, setInstallPromptEvent] = useState(null)
  const [isStandalone, setIsStandalone] = useState(false)
  const [isDismissed, setIsDismissed] = useState(false)
  const [doNotShowAgain, setDoNotShowAgain] = useState(false)
  const [hiddenByPreference, setHiddenByPreference] = useState(false)
  const translations = useStore.translations()[0]
  const stateLanguage = useStore.language()[0]
  const storageKey = "svb-hide-install-overlay"
  const debugKey = "svb-install-debug"
  const suppressDays = 90
  const suppressDurationMs = suppressDays * 24 * 60 * 60 * 1000

  const debugLog = (...args) => {
    if (typeof window === "undefined") return
    if (localStorage.getItem(debugKey) !== "true") return
    // Opt-in diagnostics for production troubleshooting.
    console.log("[PWAInstallOverlay]", ...args)
  }

  const getPreferenceState = () => {
    if (typeof window === "undefined") return false
    const raw = localStorage.getItem(storageKey)
    if (!raw) return false

    // Backward compatibility for old boolean format.
    if (raw === "true") {
      const hideUntil = Date.now() + suppressDurationMs
      localStorage.setItem(storageKey, JSON.stringify({ hideUntil }))
      return true
    }

    try {
      const parsed = JSON.parse(raw)
      if (!parsed || typeof parsed.hideUntil !== "number") {
        localStorage.removeItem(storageKey)
        return false
      }
      if (Date.now() < parsed.hideUntil) return true
      localStorage.removeItem(storageKey)
      return false
    } catch (error) {
      localStorage.removeItem(storageKey)
      return false
    }
  }

  const setInstallAvailability = value => {
    if (typeof window === "undefined") return
    window.__svbInstallPromptAvailable = value
    window.dispatchEvent(new CustomEvent("svb-install-availability-changed"))
    debugLog("availability changed", { available: value })
  }

  useEffect(() => {
    if (typeof window === "undefined") return undefined

    const hideInstallOverlay = getPreferenceState()
    setHiddenByPreference(hideInstallOverlay)
    const standalone = isStandaloneMode()
    setIsStandalone(standalone)
    debugLog("init", {
      hideInstallOverlay,
      standalone,
      hasDeferredPrompt: !!window.__svbDeferredInstallPrompt,
    })
    setInstallAvailability(!!window.__svbDeferredInstallPrompt)

    const handleBeforeInstallPrompt = event => {
      event.preventDefault()
      window.__svbDeferredInstallPrompt = event
      setInstallAvailability(true)
      setInstallPromptEvent(event)
      setIsDismissed(hideInstallOverlay)
      console.log("[PWAInstallOverlay] beforeinstallprompt event captured")
      debugLog("beforeinstallprompt captured", {
        hideInstallOverlay,
      })
    }

    const handleAppInstalled = () => {
      window.__svbDeferredInstallPrompt = null
      setInstallAvailability(false)
      setInstallPromptEvent(null)
      setIsStandalone(true)
      debugLog("appinstalled")
    }

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt)
    window.addEventListener("appinstalled", handleAppInstalled)
    const recheckTimer = setTimeout(() => {
      const available = !!window.__svbDeferredInstallPrompt
      setInstallAvailability(available)
      if (!available) {
        console.log(
          "[PWAInstallOverlay] Install currently unavailable on this browser session"
        )
      }
      debugLog("delayed recheck", {
        available,
        standalone: isStandaloneMode(),
        hiddenByPreference: getPreferenceState(),
      })
    }, 3000)

    return () => {
      clearTimeout(recheckTimer)
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      )
      window.removeEventListener("appinstalled", handleAppInstalled)
    }
  }, [])

  const dismissOverlay = () => {
    if (doNotShowAgain && typeof window !== "undefined") {
      const hideUntil = Date.now() + suppressDurationMs
      localStorage.setItem(storageKey, JSON.stringify({ hideUntil }))
      setHiddenByPreference(true)
      debugLog("dismissed with expiry", { suppressDays, hideUntil })
    }
    setIsDismissed(true)
    debugLog("dismissed for session")
  }

  const onInstallClick = async () => {
    if (!installPromptEvent) return

    const deferredPrompt =
      window.__svbDeferredInstallPrompt || installPromptEvent
    deferredPrompt.prompt()
    await deferredPrompt.userChoice
    debugLog("install choice resolved")
    window.__svbDeferredInstallPrompt = null
    setInstallAvailability(false)
    setInstallPromptEvent(null)
  }

  useEffect(() => {
    debugLog("state", {
      hasPromptEvent: !!installPromptEvent,
      isStandalone,
      isDismissed,
      hiddenByPreference,
    })
  }, [installPromptEvent, isStandalone, isDismissed, hiddenByPreference])

  if (!installPromptEvent || isStandalone || isDismissed || hiddenByPreference) {
    return null
  }

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: 1300,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(0, 0, 0, 0.35)",
      }}
    >
      <div
        style={{
          background: "#1f2937",
          color: "#ffffff",
          borderRadius: "10px",
          padding: "16px",
          boxShadow: "0 4px 16px rgba(0, 0, 0, 0.25)",
          width: "min(320px, calc(100% - 32px))",
          textAlign: "center",
          position: "relative",
        }}
      >
        <button
          type="button"
          onClick={dismissOverlay}
          aria-label={getTranslation(
            translations,
            stateLanguage,
            "UI",
            "install_prompt_close"
          )}
          style={{
            position: "absolute",
            top: "8px",
            right: "8px",
            border: "none",
            background: "transparent",
            color: "#ffffff",
            cursor: "pointer",
            fontSize: "20px",
            lineHeight: 1,
          }}
        >
          ×
        </button>
        <p style={{ margin: "0 0 12px 0", paddingRight: "18px" }}>
          {getTranslation(
            translations,
            stateLanguage,
            "UI",
            "install_prompt_title"
          )}
        </p>

        <button
          type="button"
          onClick={onInstallClick}
          style={{
            cursor: "pointer",
            background: "#2563eb",
            color: "#ffffff",
            border: "none",
            borderRadius: "8px",
            padding: "8px 12px",
            fontWeight: 600,
          }}
        >
          {getTranslation(
            translations,
            stateLanguage,
            "UI",
            "install_prompt_button"
          )}
        </button>
        <br />
        <label
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "12px",
            fontSize: "13px",
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={doNotShowAgain}
            onChange={event => setDoNotShowAgain(event.target.checked)}
          />
          {getTranslation(
            translations,
            stateLanguage,
            "UI",
            "install_prompt_do_not_show_again"
          )}
        </label>
      </div>
    </div>
  )
}

export default PWAInstallOverlay
